// iter-35a-fix — Reusable Add-to-Home-Screen popup.
//
// Shared between FanPortal (iter-33) and Creator Dashboard (iter-35a-fix).
// Mobile-only. Shows on first signup AND on second visit if not dismissed.
// localStorage key is namespaced ('mysilo_a2hs_dismissed') so dismissal
// persists across both portals (a creator who dismissed it as a fan won't
// see it again as a creator).

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { DeviceMobile, X } from "@phosphor-icons/react";

function detectMobilePlatform() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = (navigator.userAgent || "").toLowerCase();
  const isIOS =
    /iphone|ipad|ipod/.test(ua) ||
    (/macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (isIOS) return "ios";
  if (/android/.test(ua)) return "android";
  return "desktop";
}

/**
 * @param {object} props
 * @param {string} props.context — 'fan' | 'creator' — only used to namespace
 *                                 the session-storage flag so a creator
 *                                 sees it once on dashboard even if they
 *                                 dismissed-this-session as a fan.
 * @param {boolean} props.triggerNow — render now (e.g., right after signup).
 * @param {string=} props.siloName — if provided, the popup tells the user
 *                                 the icon will open straight to this silo.
 *                                 If omitted, the popup warns them to be
 *                                 inside a silo first so the icon is
 *                                 silo-specific.
 */
export default function A2HSPopup({ context = "fan", triggerNow = false, siloName }) {
  const [open, setOpen] = useState(false);
  const platform = detectMobilePlatform();
  const sessionKey = `a2hs_${context}_shown`;
  const persistKey = "mysilo_a2hs_dismissed";
  // May 24, 2026 — auto-detect silo context from the URL so callers that
  // forgot to pass `siloName` still show the right copy. We treat both
  // /s/<slug> (public Q&A) and /dashboard/silo/<id> (creator-side tabs)
  // as "inside a silo".
  const inSiloContext = !!siloName
    || (typeof window !== "undefined" && /^\/(?:s|dashboard\/silo)\//.test(window.location.pathname));

  useEffect(() => {
    if (platform === "desktop") return;
    // May 31, 2026 — only ever prompt from INSIDE a silo. The old "open
    // one of your silos first" variant (shown from the creator dashboard)
    // was removed at Randy's request — adding the icon from the dashboard
    // only bookmarked the dashboard and confused people. The home-screen
    // prompt now appears solely from within a silo's Q&A, where it works.
    if (!inSiloContext) return;
    if (typeof localStorage !== "undefined" && localStorage.getItem(persistKey)) return;
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(sessionKey)) return;
    if (triggerNow) {
      setOpen(true);
      try { sessionStorage.setItem(sessionKey, "1"); } catch { /* ignore */ }
    }
  }, [platform, triggerNow, sessionKey, inSiloContext]);

  const dismiss = (forever = false) => {
    setOpen(false);
    if (forever) {
      try { localStorage.setItem(persistKey, "1"); } catch { /* ignore */ }
    }
  };

  if (!open || platform === "desktop" || !inSiloContext) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" data-testid={`a2hs-modal-${context}`}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="modal-safe bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative"
      >
        <button onClick={() => dismiss(false)} className="absolute top-3 right-3 text-[#6B6A68] hover:text-[#1C1B1A]" data-testid={`a2hs-close-x-${context}`}>
          <X size={16} />
        </button>
        <div className="w-12 h-12 rounded-full bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center mx-auto">
          <DeviceMobile size={22} weight="duotone" />
        </div>
        <h2 className="font-serif-display text-xl text-[#1C1B1A] mt-3 text-center">
          {siloName ? `Add ${siloName} to your home screen` : "Add this silo to your home screen"}
        </h2>
        <p className="text-sm text-[#6B6A68] mt-2 text-center leading-relaxed">
          One tap from your home screen jumps straight into this silo's public Q&A — no login needed.
        </p>

        {platform === "ios" && (
          <ol className="text-sm text-[#1C1B1A] mt-5 space-y-2.5 bg-[#FDFCF8] border hairline rounded-md p-4" data-testid={`a2hs-ios-steps-${context}`}>
            <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">1.</span> Tap the <strong>Share icon</strong> at the bottom of Safari.</li>
            <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">2.</span> Scroll down → tap <strong>"Add to Home Screen"</strong>.</li>
            <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">3.</span> Tap <strong>Add</strong> in the top-right corner.</li>
          </ol>
        )}
        {platform === "android" && (
          <ol className="text-sm text-[#1C1B1A] mt-5 space-y-2.5 bg-[#FDFCF8] border hairline rounded-md p-4" data-testid={`a2hs-android-steps-${context}`}>
            <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">1.</span> Tap the <strong>three-dot menu</strong> in Chrome.</li>
            <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">2.</span> Tap <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>.</li>
            <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">3.</span> Confirm with <strong>Install</strong> / <strong>Add</strong>.</li>
          </ol>
        )}

        <p className="text-[12px] text-[#6B6A68] mt-3 text-center italic leading-relaxed" data-testid={`a2hs-notif-note-${context}`}>
          Heads up: for silo notifications to work, you must add it to your home screen from <strong>inside the silo's Q&amp;A</strong> — right here, where you are now.
        </p>

        <div className="flex flex-col gap-2 mt-5">
          <button
            onClick={() => dismiss(false)}
            data-testid={`a2hs-later-btn-${context}`}
            className="w-full text-sm bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] py-2.5 rounded-md"
          >
            Got it — I'll do this now
          </button>
          <button
            onClick={() => dismiss(true)}
            data-testid={`a2hs-never-btn-${context}`}
            className="w-full text-xs text-[#6B6A68] hover:text-[#C16E5A] py-1.5"
          >
            Don't show this again
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export { detectMobilePlatform };
